/**
 * OnboardingSuccess.tsx
 * Final screen shown after successfully completing the onboarding flow.
 * Mode-aware: shows companion video + "Start Chatting" in companion mode,
 * or a simple confirmation + "Go to Dashboard" in ILE mode.
 */
import React from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { CompanionVideoPlayer } from "../shared/CompanionVideoPlayer";
import { colors, spacing, spacingStyles } from "../../../styles/design-system";

export interface OnboardingSuccessProps {
  onComplete: () => void;
  isCompanionMode?: boolean;
}

function OnboardingSuccess({ onComplete, isCompanionMode = true }: OnboardingSuccessProps) {
  const titleKey = isCompanionMode ? "companion_onboarding_success_title" : "ile_onboarding_success_title";
  const hintKey = isCompanionMode ? "companion_onboarding_success_video_hint" : "ile_onboarding_success_subtitle";
  const settingsHintKey = isCompanionMode
    ? "companion_onboarding_success_settings_hint"
    : "ile_onboarding_success_settings_hint";
  const ctaKey = isCompanionMode ? "buttons.companion_onboarding_success_cta" : "buttons.ile_onboarding_success_cta";

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={spacingStyles.pLg}>
        {/* Header */}
        <View style={styles.header}>
          <CustomText
            text={titleKey}
            size="xl"
            weight="bold"
            style={{ color: colors.grey[900], marginBottom: spacing.xs }}
          />
          <CustomText text={hintKey} size="m" style={{ color: colors.grey[600] }} />
        </View>

        {/* Video Player - companion mode only */}
        {isCompanionMode && <CompanionVideoPlayer />}

        {/* Settings Hint */}
        <View style={styles.settingsHint}>
          <CustomText text={settingsHintKey} size="m" style={{ color: colors.grey[600], textAlign: "center" }} />
        </View>

        {/* CTA Button */}
        <View style={styles.buttonContainer}>
          <CustomButton text={ctaKey} onPress={onComplete} styleType="primary" style={{ width: "100%" }} />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    alignItems: "center",
    marginBottom: spacing.sm,
  },
  settingsHint: {
    marginVertical: spacing.lg,
  },
  buttonContainer: {
    marginTop: spacing.sm,
  },
});

export default OnboardingSuccess;
